import React from 'react';
/**
 * Audio processing utilities for noise suppression and filtering
 */

/**
 * Applies noise suppression to a LiveKit audio track
 * @param {LocalAudioTrack} track - The LiveKit audio track to process
 * @returns {Promise<MediaStreamAudioSourceNode>} - The processed audio source node
 */
export async function applyNoiseSuppressionToTrack(track) {
  if (!track || !track.mediaStreamTrack) {
    console.warn('No valid audio track provided for noise suppression');
    return null;
  }

  try {
    // Create audio context
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();

    // Create source from the track's MediaStreamTrack
    const mediaStream = new MediaStream([track.mediaStreamTrack]);
    const source = audioContext.createMediaStreamSource(mediaStream);

    // Create a gain node to control volume
    const gainNode = audioContext.createGain();
    gainNode.gain.value = 1.0;

    // Create a biquad filter for low frequency noise reduction
    const lowFilter = audioContext.createBiquadFilter();
    lowFilter.type = 'highpass';
    lowFilter.frequency.value = 100; // Filter out frequencies below 100Hz

    // Create a biquad filter for high frequency noise reduction
    const highFilter = audioContext.createBiquadFilter();
    highFilter.type = 'lowpass';
    highFilter.frequency.value = 8000; // Filter out frequencies above 8kHz

    // Create a dynamics compressor to even out volume levels
    const compressor = audioContext.createDynamicsCompressor();
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;

    // Connect the audio processing graph
    source.connect(lowFilter);
    lowFilter.connect(highFilter);
    highFilter.connect(compressor);
    compressor.connect(gainNode);

    // IMPORTANT: For now, we're just analyzing the audio, not replacing it
    // This ensures audio still works while we debug the processing
    // Store references for cleanup
    if (!track._audioProcessing) {
      track._audioProcessing = {
        context: audioContext,
        nodes: [source, lowFilter, highFilter, compressor, gainNode],
      };
    }

    return source;
  } catch (error) {
    console.error('Error applying noise suppression:', error);
    return null;
  }
}

/**
 * Stops audio processing and cleans up resources
 * @param {LocalAudioTrack} track - The track with audio processing applied
 */
export function stopAudioProcessing(track) {
  if (track && track._audioProcessing) {
    if (track._audioProcessing.context) {
      track._audioProcessing.context.close();
    }
    delete track._audioProcessing;
  }
}

/**
 * Creates a noise gate to filter out background noise below a threshold
 * @param {AudioContext} audioContext - The Web Audio context
 * @param {number} threshold - The noise gate threshold (dB, typically -50 to -20)
 * @param {number} attack - Attack time in seconds
 * @param {number} release - Release time in seconds
 * @returns {GainNode} - A gain node that implements the noise gate
 */
export function createNoiseGate(
  audioContext,
  threshold = -40,
  attack = 0.01,
  release = 0.1
) {
  const gainNode = audioContext.createGain();
  let lastVolume = 0;

  // Create an analyzer to detect volume
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 1024;
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  // Process the audio to apply the noise gate
  const processAudio = () => {
    analyser.getByteTimeDomainData(dataArray);

    // Calculate RMS volume
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      const amplitude = (dataArray[i] - 128) / 128;
      sum += amplitude * amplitude;
    }
    const rms = Math.sqrt(sum / bufferLength);
    const db = 20 * Math.log10(rms);

    // Apply noise gate
    const targetVolume = db < threshold ? 0 : 1;
    const timeConstant = targetVolume > lastVolume ? attack : release;

    // Smooth the volume change
    lastVolume = lastVolume + (targetVolume - lastVolume) * timeConstant;
    gainNode.gain.value = lastVolume;

    // Continue processing
    requestAnimationFrame(processAudio);
  };

  processAudio();
  return gainNode;
}
